import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { RobbieMark } from '../../ui';
import { splitSentEmail } from '../../../hooks/outreachMappers';

/**
 * The email behind a query. The buyer wrote the ask; Robbie wrote and sent the
 * email — so the email is a record, not a draft. It stays collapsed under the
 * bubble and opens read-only: no fields, no edit, nothing to approve after the
 * fact, because it has already gone.
 */
interface SentEmailPeekProps {
  /** The stored outbound message: subject line first, then the body. */
  email: string;
}

const SentEmailPeek: React.FC<SentEmailPeekProps> = ({ email }) => {
  const [open, setOpen] = useState(false);
  const { subject, body } = splitSentEmail(email);

  return (
    <div className="mt-s2 border-t border-line-divider-soft pt-s2">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        className="inline-flex items-center gap-s2 text-[10px] font-semibold text-robbie-ink hover:text-robbie"
      >
        <RobbieMark size="xxs" aria-label="Sent by Robbie" />
        {open ? 'Hide the email Robbie sent' : 'View the email Robbie sent'}
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="mt-s2 rounded-control border border-robbie-border-soft bg-surface px-[10px] py-s2">
          {subject && (
            <div className="mb-s2 border-b border-line-divider-soft pb-s2">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-muted">Subject</span>
              <div className="text-meta font-semibold leading-[1.45] text-ink">{subject}</div>
            </div>
          )}
          <div className="whitespace-pre-wrap text-meta leading-[1.55] text-ink-body">{body}</div>
        </div>
      )}
    </div>
  );
};

export default SentEmailPeek;
